import type {
    AddFileGlobalFunctions,
    Client,
    ClientInputs,
    ContactFieldKey,
    EditAvailableClientValues,
    MultipleSelectClient,
    Role,
} from '@/company-portal/add-file/add-file.types';
import { handleEmailChangeForSigners } from './add-file-documents';
import { ClientContext, getAvailableClients, setAvailableClients, updateRecipientsLists } from './available-clients';
import { clearFormControlErrorMessageHandler } from './clear-form-errors';
import { ADD_CLIENT_FORM_SELECTOR } from './selectors';
import { formatE164ToNational } from '@/shared/utils/phone-number';
import { tpsClientInfo, userAddedClientInfo } from './use-client-info-forms';

declare global {
    interface Window extends AddFileGlobalFunctions {}
}

const validationMessages = {
    requiredEmail: 'Please specify an email address.',
    email: 'Email format is invalid.',
    phone: 'Phone number format is invalid.',
};

export const getAllClientInputs = (formSelector: string): ClientInputs => {
    const inputs = $(formSelector).serializeArray();
    return {
        emails: inputs.filter(input => input.name.startsWith('email')),
        phones: inputs.filter(input => input.name.startsWith('phone')),
        roles: inputs.filter(input => input.name.startsWith('role')) as { name: string; value: Client['role'] }[],
        firsts: inputs.filter(input => input.name.startsWith('first')),
        lasts: inputs.filter(input => input.name.startsWith('last')),
    };
};

// Iterate over all email inputs to build array of Clients
export const getClientsFromEnabledEmailInputs = (formSelector: string, existingClients?: Client[]): MultipleSelectClient[] => {
    const inputs = getAllClientInputs(formSelector);
    const clients: MultipleSelectClient[] = [];

    inputs.emails.forEach((emailInput, index) => {
        // see if we have an existing client to grab properties from
        const existingClient = existingClients?.find(client => client.email === emailInput.value);
        if (inputs.roles[index]?.value) {
            const client: Client = {
                id: emailInput.name.match(/\[([0-9]*?)\]/)?.[1] ?? '',
                email: emailInput.value,
                phone: inputs.phones[index].value,
                role: inputs?.roles[index]?.value || 'buyer', // since theres a slight delay in setting the role, we default if not provided
                first: inputs.firsts[index].value,
                last: inputs.lasts[index].value,
            };
            if (client.email) {
                const clientId = client.id || '';
                clients.push({
                    id: clientId,
                    value: clientId,
                    text: buildClientTextLine(client, clientId),
                    email: client.email,
                    phone: client.phone,
                    role: client.role,
                    first: client.first,
                    last: client.last,
                    identity_verified: existingClient?.identity_verified ?? false,
                });
            }
        }
    });

    return clients;
};

export const buildClientTextLine = (client: Client, id: string): string => {
    const role: string = client.role || '';
    let firstRowText: string;
    let secondRowText: string;

    // client.phone is stored in E164 so the Edit sheet can resolve the country; display it nationally.
    const displayPhone = client.phone ? formatE164ToNational(client.phone) : '';

    if (client.first || client.last) {
        firstRowText = client.first + ' ' + client.last;
        secondRowText = client.email + (displayPhone ? ', ' + displayPhone : '');
    } else {
        firstRowText = client.email;
        secondRowText = displayPhone;
    }

    return $('#client-text-line-template')
        .html()
        .replaceAll(`'{role}'`, role || '')
        .replaceAll(`'{firstRowText}'`, firstRowText || '')
        .replaceAll(`'{secondRowText}'`, secondRowText || '')
        .replaceAll(`'{id}'`, id);
};

export const buildClientSummaryTextLine = (client: Client): string => {
    const role: string = client.role || '';
    let firstRowText: string;

    if (client.first || client.last) {
        firstRowText = client.first + ' ' + client.last;
    } else {
        firstRowText = client.email;
    }

    return $('#client-summary-text-line-template')
        .html()
        .replaceAll(`'{role}'`, role || '')
        .replaceAll(`'{firstRowText}'`, firstRowText || '');
};

export const setClientTextToSummaryTextLine = (clients: MultipleSelectClient[]): MultipleSelectClient[] =>
    $.map(clients, (client: MultipleSelectClient) => {
        client.text = buildClientSummaryTextLine(client);
        return client;
    });

export const prepareModalForFormClientEditing = (contactFields: ContactFieldKey[], editedClientId: Client['id']) => {
    // For each input field, populate the 'edit' input field with the current input data for the client being edited
    // e.g. input:email.2 => input:email.edit
    $.each(contactFields, (_idx: number, contactField: ContactFieldKey) => {
        const currentContactData = $(`input[name='${contactField}[${editedClientId}]']`).val() || '';
        $(`input[data-id='${contactField}.edit']`).val(currentContactData);
    });

    // Get the role from Client Form Fields (select dropdown)
    const currentRole = $(`input[name='role[${editedClientId}]']`).val();
    // Set the role in Edit Client Modal (radio buttons)
    $(`input[data-id='role.edit'][value='${currentRole}']`).click().blur();

    const $editNewClientModal = $('#editNewClientModal');
    $editNewClientModal.modal('show');
};

export const syncClientInfoState = (clientId: Client['id'], values: EditAvailableClientValues) => {
    const clientInfoMap = userAddedClientInfo.has(clientId) ? userAddedClientInfo : tpsClientInfo;
    const oldEmail = clientInfoMap.get(clientId)?.email;
    if (!values.email) {
        // When a client's email is cleared via the Edit modal, they'll also be removed from whichever map owns them (userAddedClientInfo or tpsClientInfo)
        clientInfoMap.delete(clientId);
    } else {
        clientInfoMap.set(clientId, {
            id: clientId,
            first: values.first,
            last: values.last,
            email: values.email,
            phone: values.phone,
            role: values.role,
        });
    }

    // If email changes, we must update saved edited eSign documents
    if (oldEmail !== values.email) {
        handleEmailChangeForSigners(String(clientId));
    }
};

export const handleEditedFormClient = (contactFields: ContactFieldKey[], editedClientId: Client['id']) => {
    // We will need to update our 'imported' clients with our edited client info.
    const clients = getAvailableClients(ClientContext.NEW);
    const client = clients.find(client => Number(client.value) === Number(editedClientId));

    // For each 'edit' input field, overwrite the edited client input field with the edited data.
    // e.g. input:email.edit => input:email.2
    // Also update the edited client for resetting our 'imported' available clients. This allows us to
    // rebuild the select clients selector with the updated/edited client
    $.each(contactFields, (_idx: number, contactField: ContactFieldKey) => {
        const editedContactData = $(`input[data-id='${contactField}.edit']`).val() || '';
        $(`input[name='${contactField}[${editedClientId}]']`).val(editedContactData);
        if (client) {
            client[contactField] = editedContactData?.toString();
        }
    });

    // Get role from Edit Client Modal (radio buttons)
    const newRole = $(`input[data-id='role.edit']:checked`).val();
    // Set the role in Client Form Fields (select dropdown)
    $(`input[name='role[${editedClientId}]']`).val(`${newRole}`);

    if (client) {
        client['role'] = newRole as Role;
        // Rebuild the text that will show in the select clients selector for the edited client
        client['text'] = buildClientTextLine(client, `${editedClientId}`);
    }

    // Update our available 'imported' clients with the edited client
    setAvailableClients(ClientContext.NEW, clients);

    // Update the drop downs with the edited client
    // Existing behavior: if the email is cleared, that client gets removed from the Recipients dropdown. See: updateRecipientsLists > getClientsFromEnabledEmailInputs > `if (client.email)`
    updateRecipientsLists();

    // Update client info state
    if (client) {
        syncClientInfoState(client.id, client);
    }

    toastr.success('Contact information updated');
    const $editNewClientModal = $('#editNewClientModal');
    $editNewClientModal.modal('hide');
};

export const enableClientInputs = (clientElement: JQuery<HTMLElement>) => {
    toggleDisabledOnClientInputs(clientElement, false);
};

export const disableClientInputs = (clientElement: JQuery<HTMLElement>) => {
    toggleDisabledOnClientInputs(clientElement, true);
};

export const toggleDisabledOnClientInputs = (clientElement: JQuery<HTMLElement>, disabled: boolean) => {
    clientElement.find('input').prop('disabled', disabled);
};

export const clearMainFormClients = (formSelector: JQuery.Selector = ADD_CLIENT_FORM_SELECTOR) => {
    clearClients(formSelector);
    $(formSelector).find('.add_client_btn').trigger('click');
    updateRecipientsLists(formSelector);
};

export const addClientButtonListener = (formSelector: JQuery.Selector = ADD_CLIENT_FORM_SELECTOR, addClientNow?: boolean) => {
    const addClientBtn = $(formSelector).find('.add_client_btn')[0];
    addClientBtn?.addEventListener('click', function () {
        addClient(this, true);
    });

    // Init client input 0. Must be done after '$("#addClientForm").validate();'
    if (addClientNow) {
        addClientBtn?.click();
    }
};

export const addClient = (that: HTMLElement, addOnChangeEvent: boolean) => {
    addClientWithRole(that, addOnChangeEvent);
};

export const addClientWithRole = (that: HTMLElement, addOnChangeEvent: boolean, role?: Role): JQuery<HTMLElement> => {
    const $form = $(that).parents('form');
    const $lastClientEmail = $form.find('.client_contact_container .client-email[data-counter]').last();
    const counter = parseInt($lastClientEmail.attr('data-counter') || '-1') + 1;

    const newClientHtml = $('#client-contact-template').html().replaceAll('{counter}', counter.toString());

    const newClientElement = $(newClientHtml).appendTo($form.find('.clients-container'));
    if (role) {
        // 'role' must be selected here so that the updates to the recipients list already have 'role' set for the
        // default selections (e.g. wireDocRecipients are auto-selected for the roles 'buyer' and 'lender'
        const roleInput = newClientElement.find('.client-role[value=' + role.toLowerCase() + ']');
        roleInput.trigger('click').trigger('blur');
    }

    const clientInputElements = newClientElement.find('.client-first, .client-last, .client-email, .client-phone, .client-role');
    clientInputElements.keyup(function () {
        clearFormControlErrorMessageHandler(this, '.client-contact-input');
    });

    if (addOnChangeEvent) {
        clientInputElements.on('change', function () {
            updateRecipientsLists('#' + $form[0].id);

            // If email changes, we must update the edited eSign documents
            if ($(this).hasClass('client-email')) {
                handleEmailChangeForSigners(`${$(this).data('counter')}`);
            }
        });
    }

    setEmailValidation(newClientElement.find('.client-email'));
    setPhoneValidation(newClientElement.find('.client-phone'));

    return newClientElement;
};

export const removeClient = (that: HTMLElement) => {
    const formId = '#' + $(that).parents('form')[0].id;
    $(that).parents('.client_contact_container').remove();
    updateRecipientsLists(formId);
};
window.removeClient = removeClient;

export const clearClients = (formID: string) => {
    $(formID).find('.client_contact_container').remove();
};

export const initFormValidation = (formSelector: JQuery.Selector) => {
    $(formSelector).validate({});
};

export const setEmailValidation = ($inputElement: JQuery) => {
    $inputElement.rules('add', {
        required: true,
        email: true,
        messages: {
            required: validationMessages.requiredEmail,
            email: validationMessages.email,
        },
    });
};

export const setPhoneValidation = ($inputElement: JQuery) => {
    if ($inputElement && $inputElement.length) {
        $inputElement
            .on('keyup change', event => {
                resetTelInputErrors(event.target);
            })
            .on('blur', event => {
                validateTelInput(event.target);
            })
            .intlTelInput();
    }
};

export const validateTelInput = (element: HTMLElement) => {
    const $element = $(element);
    const $elementErrorLabel = $element.parent().next();
    const $elVal = $.trim($element.val()?.toString() ?? '');

    if ($elVal && !$element.intlTelInput('isValidNumber')) {
        $element.addClass('error');
        $elementErrorLabel.text(validationMessages.phone);
    }
};

export const resetTelInputErrors = (element: HTMLElement) => {
    const $element = $(element);
    const $elementErrorLabel = $element.parent().next();
    $element.removeClass('error');
    $elementErrorLabel.text('');
};

// ID of the client being edited in the Edit Client modal
let editedClientId: Client['id'] = '';
export const setEditedClientId = (clientId: Client['id']) => (editedClientId = clientId);
export const getEditedClientId = () => editedClientId;

export const EDIT_CLIENT_FIELDS: ContactFieldKey[] = ['first', 'last', 'email', 'phone'];
